import React from 'react'
import PropTypes from 'prop-types'
import classNames from 'classnames'

/**
 * The Callout component displays a prominent message with a call to action.
 * @see https://bronson.vwfs.tools/default/components/detail/bronson-callout.html
 *
 * @param {React.ReactNode} [children] - The content to display inside the Callout component.
 * @param {string} [className] - Additional CSS classes on the component element.
 * @param {('article'|'aside')} [elementType='article'] - The Callout component should be implemented as `<article>` or `<aside>` element.
 * @param {(2|3|4|5|6)} [headingLevel=4] - The title can be a `<h2>` to `<h6>` element.
 * @param {{alt?: string, className?: string, sizes?: string, src?: string, srcSet?: string[]}} [media] - Optional image media element.
 * @param {string} title - The title of the {@link Callout} component.
 * @param {string} [testId] - The `[data-testid]` attribute on the component element.
 * @param {object} [otherProps] - Other props passed to the component element.
 * @returns {JSX.Element} - The Callout component.
 * @constructor
 */
export function Callout({
  children,
  className,
  elementType = 'article',
  headingLevel = 4,
  media,
  title,
  testId,
  ...otherProps
}) {
  const classNameList = classNames('c-callout', className).trim()

  const ComponentElement = `${elementType}`
  const HeadingElement = `h${headingLevel}`

  const renderMedia = () => {
    if (media) {
      const imageClassName = classNames('c-callout__image', media?.className).trim()

      return (
        <div className="c-callout__media">
          {/* Keep this order of merging to override {@link media.className}. */}
          {/* eslint-disable-next-line jsx-a11y/alt-text */}
          <img {...media} className={imageClassName} />
        </div>
      )
    }

    return null
  }

  return (
    <ComponentElement className={classNameList} data-theme="brand" data-testid={testId} {...otherProps}>
      {renderMedia()}
      <HeadingElement className="c-callout__title">{title}</HeadingElement>
      {children}
    </ComponentElement>
  )
}

Callout.propTypes = {
  children: PropTypes.node, // Bronson template: 'text' and 'buttons'. Use {@link Paragraph} and {@link Button} components.
  className: PropTypes.string, // Additional CSS classes on the component element.
  /**
   * Limit the component element to `<article>` or `<aside>`.
   */
  elementType: PropTypes.oneOf(['article', 'aside']),
  /**
   * Limit the title to the elements `<h2>` to `<h6>`.
   */
  headingLevel: (props, propName, componentName, ...rest) => {
    if (props[propName] && props[propName] !== undefined && typeof props[propName] !== 'number') {
      return new Error(`Invalid value of prop \`${propName}\` supplied to \`${componentName}\`, expected \`number\`.`)
    }
    if (props[propName] < 2 || props[propName] > 6) {
      return new Error(
        `Invalid value of prop \`${propName}\` supplied to \`${componentName}\`, accepts only a range from \`2\` to \`6\`.`
      )
    }
    return PropTypes.number(props, propName, componentName, ...rest)
  },
  media: PropTypes.shape({
    alt: PropTypes.string,
    className: PropTypes.string,
    sizes: PropTypes.string,
    src: PropTypes.string,
    srcSet: PropTypes.arrayOf(PropTypes.string),
  }), // Bronson template: 'media'.
  title: PropTypes.string.isRequired, // Bronson template: 'title'.
  testId: PropTypes.string, // Added for the `[data-testid]` attribute.
}
